<template>
  <div class="mb-3">
    <label for="statusFilter" class="block mb-2 font-semibold">Sending Status:</label>
    <Dropdown
      id="statusFilter"
      v-model="selectedStatus"
      :options="statusOptions"
      option-label="label"
      option-value="value"
      class="w-full"
      :disabled="isSavableStateProcessing"
      placeholder="Select Status"
      @change="onStatusChange"
    />
  </div>
  <h1 class="font-weight-bold" style="width: 100%; background-color: #f0f0f0; padding: 10px 20px; margin: 0;">
    Unet Emails and Alternative Notifications
  </h1>
  <PortalTable ref="unet_emails_index" table-id="unet_emails_index"
    :table-data="records"
    :total-records="totalRecords"
    :page="pageNumber"
    :page-count="pageCount"
    :loading="isSavableStateProcessing"
    :filter-values="filterValues"
    filter-display="row"
    empty="No record found!"
    dataKey="id"
    @on-sort="handleSortChange"
    @on-page="handlePageChange"
    @on-filter-change="handleFilterChange">
    <template #columns>    
      <Column expander style="width: 5rem" />       
      <Column field="email_type" header="Email Type" sortable
          filter-field="email_type"
          :filter-menu-style="{ width: '14rem' }"
          style="min-width: 14rem"
          :show-filter-menu="false">
          <template #filter="{ filterModel = { value: null }, filterCallback }">
            <InputText v-model="filterModel.value"
              type="text"
              :readonly="isSavableStateProcessing"
              class="p-column-filter form-control"
              placeholder="Search by Email Type"
              @input="filterCallback()" />
          </template>
        </Column>
      <Column field="recipient" header="Recipient" sortable
          filter-field="recipient"
          :filter-menu-style="{ width: '14rem' }"
          style="min-width: 14rem"
          :show-filter-menu="false">
          <template #filter="{ filterModel = { value: null }, filterCallback }">
            <InputText v-model="filterModel.value"
              type="text"
              :readonly="isSavableStateProcessing"
              class="p-column-filter form-control"
              placeholder="Search by Recipient"
              @input="filterCallback()" />
          </template>
      </Column>
      <Column field="donor_code" header="Donor" style="min-width: 70px;" />       
      <Column field="organ_name" header="Organ" style="min-width: 70px;" />       
      <Column field="transplant_center_code" header="TxC" style="min-width: 70px;" />       
      <Column field="opo_code" header="OPO" style="min-width: 70px;" />
      <Column field="match_code" header="Match" style="min-width: 70px;" />  
      <Column field="subject" header="Subject" style="min-width: 70px;" />  
      <Column field="seq" header="Seq" style="min-width: 70px;" />     
      <Column field="expedited" header="Expedited" style="min-width: 70px;" />         
      <Column field="email_created_date" header="Email Created Date" sortable style="min-width: 70px;" />     
      <Column field="email_msg_date_et" header="Email Message Date" sortable
        style="min-width: 160px"
        :show-filter-menu="false"
        :filter-menu-style="{ width: '15rem' }">
        <template #body="slotProps">
          {{ replaceZwithET(slotProps.data.email_msg_date_et) }}
        </template>
        <template #filter>
          <button type="button" class="btn btn-outline-secondary" title="Filter" @click="toggleFilter">
            <font-awesome-icon :icon="['fas', 'fa-filter']" aria-hidden="true" />
          </button>
          <button v-if="hasFilter" class="btn btn-outline-secondary" type="button" label="Clear Filter" title="Clear Filters" @click="clearFilters">
            <div class="fa-layers fa-fw">
              <font-awesome-icon :icon="['fas', 'fa-filter']" aria-hidden="true" /> 
              <font-awesome-icon :icon="['fas', 'slash']" transform="grow-3" />
            </div>
          </button>
          <div v-if="showFilter" class="overlay" @blur="toggleFilter">
            <span>From:</span>
            <div class="p-inputgroup">
              <span class="p-inputgroup-addon">Date</span>
              <InputText ref="from_date"
                v-model="dateFrom"
                type="date"
                class="p-column-filter"
                placeholder="Message Date From"
                @input="filterData" />                
            </div>             
            <span>To:</span>
            <div class="p-inputgroup">
              <span class="p-inputgroup-addon">Date</span>
              <InputText ref="to_date" v-model="dateTo"
                type="date"
                class="p-column-filter"
                @input="filterData" />               
            </div>
          </div>
        </template>
      </Column>
      <!-- Column for Alternative Notification Log -->
      <Column field="alternative_notification_log" header="Notification Log" style="min-width: 200px;">
        <template #body="slotProps">
          {{ slotProps?.data.alternative_notification_log?.log }}
        </template>
      </Column>
    </template>
    <!-- Row expansion slot -->
    <template #expansion="{ data }">
      <div class="p-4">
        <h5>Alternative Notifications</h5>
        <DataTable :value="data.alternative_notifications" empty="No record found!">
          <template #default>
          <Column field="type" header="Type" style="width: 150px;"></Column>
          <Column field="content" header="Content" style="width: 300px;"></Column>
          <Column field="sending_status" header="Status" style="width: 150px;"></Column>
          <Column field="status_reason" header="Error" style="width: 300px;"></Column>
          <Column field="sent_at" header="Sent At" sortable style="width: 200px;" />   
          </template>
        </DataTable>  
      </div>
    </template>
  </PortalTable>
</template>

<script>
import PortalTable from "@/src/components/shared/PortalTable.vue";
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import InputText from 'primevue/inputtext';
import Dropdown from 'primevue/dropdown';

export default {
  name: 'UnetEmails',
  components: {
    PortalTable,
    Column,
    DataTable,
    InputText,
    Dropdown,
  },
  props: {
    records: Array,  
    totalRecords: Number, 
    pageNumber: Number, 
    pageCount: Number,  
    isSavableStateProcessing: Boolean, 
    filterValues: Object, 
  },
  data() {
    return {
      search_params: {},
      showFilter: false,
      dateFrom: '',
      dateTo: '',
      selectedStatus: 'errored',
      statusOptions: [
        { label: 'Errored', value: 'errored' },
        { label: 'Dismissed', value: 'dismissed' },
        { label: 'Scheduled', value: 'scheduled' },
        { label: 'Duration Elapsed', value: 'duration_elapsed' },
        { label: 'Successful', value: 'successful' }
      ]
    };
  },
  mounted() {
    this.$emit('filterChange', { sending_status: this.selectedStatus });
  },
  methods: {
    replaceZwithET(datetimeStr) {
      return datetimeStr.endsWith('Z')
      ? datetimeStr.slice(0, -1) + ' ET'
      : datetimeStr + ' ET';
    },
    hasFilter() {
      return !!this.dateFrom || !!this.dateTo;
    },
    toggleFilter() {
      this.showFilter = !this.showFilter;
    },
    filterData() {
      // Emit the filter change event with the updated dates
      this.$emit('filterChange', {...this.search_params,
      date_from: this.dateFrom,
      date_to: this.dateTo,
      sending_status: this.selectedStatus
      });
    },
    clearFilters() {
      this.dateFrom = '';
      this.dateTo = '';
      this.showFilter = false;
      this.selectedStatus = 'errored'; // Reset to default if desired
      this.$emit('filterChange', {
        date_from: '',
        date_to: '',
        sending_status: this.selectedStatus
      });
    },
    // Emit sorting event to parent
    handleSortChange(params) {
      this.$emit('sortChange', params);
    },

    // Emit pagination event to parent
    handlePageChange(newPage) {
      this.$emit('pageChange', newPage);
    },

    // Emit filter change event to parent
    handleFilterChange(filters) {
      this.search_params = filters;
      this.$emit('filterChange', {
        ...filters,
        sending_status: this.selectedStatus,
        date_from: this.dateFrom,
        date_to: this.dateTo,
      });
    },
    onStatusChange() {
      this.$emit('filterChange', {...this.search_params,
        sending_status: this.selectedStatus,
        date_from: this.dateFrom,
        date_to: this.dateTo
      });
    }
  }
};
</script>